import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import AlertService from "@/Service/AlertService";
import { showAlert } from "./alertSlice";

const initialState = {
  message: "",
  updatedAt: null,
  status: "idle",
  error: null,
};

export const fetchSystemAlert = createAsyncThunk(
  "systemAlert/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const data = await AlertService.getAlert();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load alert message");
    }
  }
);

export const saveSystemAlert = createAsyncThunk(
  "systemAlert/save",
  async (message, { dispatch, rejectWithValue }) => {
    try {
      const data = await AlertService.updateAlert(message);
      dispatch(showAlert("success", "Alert message saved"));
      return data;
    } catch (error) {
      dispatch(showAlert("error", "Failed to save alert message"));
      return rejectWithValue(error?.message || "Failed to save alert");
    }
  }
);

const systemAlertSlice = createSlice({
  name: "systemAlert",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemAlert.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSystemAlert.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload?.message || "";
        state.updatedAt = action.payload?.updatedAt || null;
      })
      .addCase(fetchSystemAlert.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(saveSystemAlert.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(saveSystemAlert.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = action.payload?.message || "";
        state.updatedAt = action.payload?.updatedAt || null;
      })
      .addCase(saveSystemAlert.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const useSystemAlertState = () =>
  useSelector((state) => state.systemAlert);

export const useSystemAlertActions = () => {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      fetchSystemAlert: () => dispatch(fetchSystemAlert()),
      saveSystemAlert: (message) => dispatch(saveSystemAlert(message)),
    }),
    [dispatch]
  );
};

export default systemAlertSlice.reducer;
